import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lock, Sparkles, ArrowRight, Crown } from "lucide-react";

/**
 * ProRoute — PRO-only route guard.
 * - Logged-out users → /auth
 * - FREE users → full-page paywall card with /upgrade CTA
 * - PRO users → renders children
 */
const ProRoute = ({ children, featureName = "This feature", featureDescription }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <div data-testid="auth-loading">Loading...</div>;
  if (!user) return <Navigate to="/auth" replace state={{ from: location }} />;

  const isPro = user.role === "PRO" || user.subscription_status === "PRO";
  if (isPro) return children;

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/40 to-purple-50/60 flex items-center justify-center p-4"
      data-testid="pro-paywall-page"
    >
      <Card className="max-w-2xl w-full border-2 border-purple-200 shadow-xl">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-14 h-14 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl flex items-center justify-center mb-3">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold mx-auto mb-2 w-fit">
            <Crown className="w-3.5 h-3.5" />
            CLARIAS PRO ONLY
          </div>
          <CardTitle
            className="text-2xl font-bold text-slate-900"
            data-testid="pro-paywall-title"
          >
            {featureName} is locked
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-center">
          <p
            className="text-slate-600 text-sm leading-relaxed"
            data-testid="pro-paywall-description"
          >
            {featureDescription ||
              "This is a rubric-graded, examiner-trained intelligence layer reserved for CLARIAS PRO aspirants. Upgrade once and unlock it for your full UPSC journey."}
          </p>

          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 rounded-xl p-4 text-left">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-purple-600" />
              <span className="text-xs font-semibold text-purple-900 uppercase tracking-wide">
                What you unlock with PRO
              </span>
            </div>
            <ul className="space-y-1.5 text-sm text-slate-700">
              <li>• Strategy Analytics — mastery, weak topics, weekly plan</li>
              <li>• Answer Arena — daily rubric-graded mains practice + peer benchmarking</li>
              <li>• National Rank Benchmark — percentile vs every CLARIAS aspirant</li>
              <li>• Unlimited GS, Mains & Essay rubric evaluations</li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-1">
            <Button
              size="lg"
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold"
              onClick={() => (window.location.href = "/upgrade")}
              data-testid="pro-paywall-upgrade-button"
            >
              Upgrade to PRO
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => window.history.back()}
              data-testid="pro-paywall-back-button"
            >
              Go back
            </Button>
          </div>
          <p className="text-xs text-slate-400 pt-1">
            Plans start at ₹699 / month · cancel anytime
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProRoute;
